import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  FaCheckCircle, 
  FaExclamationTriangle, 
  FaChartLine, 
  FaSpinner,
  FaTrophy,
  FaWallet,
  FaList,
  FaCog,
  FaRocket,
  FaHandPointer
} from 'react-icons/fa';
import { HiOutlineTrendingUp, HiOutlineTrendingDown } from 'react-icons/hi';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import ModifyScanModal from '../components/ModifyScanModal';
import TradingHoursModal from '../components/TradingHoursModal';
import ScheduledScansSection from '../components/ScheduledScansSection';
import ReconnectBrokerModal from '../components/ReconnectBrokerModal';
import { isMarketOpen } from '../utils/tradingHours';
import { useAuth } from '../context/AuthContext';
import api from '../api';

const FnoTraderPage = () => {
  const navigate = useNavigate();
  const { triggerBrokerReconnect } = useAuth();
  
  // Loading states
  const [isBreakoutLoading, setIsBreakoutLoading] = useState(false);
  const [isBreakdownLoading, setIsBreakdownLoading] = useState(false);
  
  // Result states (for automatic mode)
  const [breakoutResult, setBreakoutResult] = useState(null);
  const [breakdownResult, setBreakdownResult] = useState(null);
  
  // Error states
  const [breakoutError, setBreakoutError] = useState(null);
  const [breakdownError, setBreakdownError] = useState(null);
  
  // Manual selection states
  const [breakoutCandidates, setBreakoutCandidates] = useState([]);
  const [breakdownCandidates, setBreakdownCandidates] = useState([]);
  const [placingOrderFor, setPlacingOrderFor] = useState(null); // Track which candidate order is being placed
  
  // Modal state
  const [isModifyModalOpen, setIsModifyModalOpen] = useState(false);
  const [currentStrategy, setCurrentStrategy] = useState('breakout');
  const [currentMode, setCurrentMode] = useState('automatic'); // 'automatic' or 'manual'
  const [isTradingHoursModalOpen, setIsTradingHoursModalOpen] = useState(false);
  const [tradingHoursDayType, setTradingHoursDayType] = useState('');
  const [refreshSchedules, setRefreshSchedules] = useState(0);
  const [isSyncingStatus, setIsSyncingStatus] = useState(false);
  const [lastSyncTime, setLastSyncTime] = useState(null);
  
  // Broker status states
  const [showPageLevelBrokerModal, setShowPageLevelBrokerModal] = useState(false);

  // ============================================================================
  // BROKER STATUS CHECK ON PAGE LOAD
  // ============================================================================

  useEffect(() => {
    const checkBrokerStatus = async () => {
      try {
        console.log('🔍 Checking broker status on FNO Trader page...');
        const response = await api.get('/api/v1/brokers/status');
        const status = response.data;
        
        console.log('📊 Broker status:', status);
        
        // Case 1: No credentials at all - redirect to broker integrations
        if (!status.has_credentials) {
          console.log('❌ No broker credentials found. Redirecting to broker integrations...');
          navigate('/broker-integrations');
          return;
        }
        
        // Case 2: Has credentials but access token is expired - show reconnect modal
        if (status.access_token_expired) {
          console.log('⏰ Access token expired. Showing reconnect modal...');
          setShowPageLevelBrokerModal(true);
          triggerBrokerReconnect();
        }
        
        // Case 3: Everything is good - continue normally
        console.log('✅ Broker status OK');
        
      } catch (error) {
        console.error('❌ Failed to check broker status:', error);
        // If check fails, allow user to proceed and handle errors during scan
      }
    };
    
    checkBrokerStatus();
  }, [navigate, triggerBrokerReconnect]); // Run once on mount

  // Listen for successful broker reconnection to close page-level modal
  useEffect(() => {
    const handleBrokerReconnected = () => {
      console.log('✅ Broker reconnected successfully. Closing page-level modal...');
      setShowPageLevelBrokerModal(false);
    };
    
    window.addEventListener('broker-reconnect-success', handleBrokerReconnected);
    
    return () => {
      window.removeEventListener('broker-reconnect-success', handleBrokerReconnected);
    };
  }, []);

  // ============================================================================
  // AUTOMATIC MODE HANDLERS
  // ============================================================================

  const handleAutoScan = async (strategy, customParams = null) => {
    // Check if market is open
    const marketStatus = isMarketOpen();
    if (!marketStatus.isOpen) {
      console.log('⚠️ Market is closed:', marketStatus.reason);
      setTradingHoursDayType(marketStatus.reason);
      setIsTradingHoursModalOpen(true);
      return;
    }
    
    const isBreakout = strategy === 'breakout';
    const setLoading = isBreakout ? setIsBreakoutLoading : setIsBreakdownLoading;
    const setError = isBreakout ? setBreakoutError : setBreakdownError;
    const setResult = isBreakout ? setBreakoutResult : setBreakdownResult;
    const setCandidates = isBreakout ? setBreakoutCandidates : setBreakdownCandidates;
    
    setLoading(true);
    setError(null);
    setResult(null);
    setCandidates([]); // Clear manual candidates if any
    
    try {
      console.log(`🚀 Starting AUTO ${strategy.toUpperCase()} Scan...`);
      
      const requestBody = {
        strategy_type: strategy,
        ...(customParams || {}),
        top_n: 3 // Always get top 3 for consistency
      };
      
      const response = await api.post('/api/v1/fno/scan-and-trade-auto', requestBody, {
        timeout: 120000 // 2 minutes timeout
      });
      
      console.log(`✅ Auto ${strategy} scan completed:`, response.data);
      setResult(response.data);
      
    } catch (err) {
      console.error(`❌ Auto ${strategy} scan failed:`, err);
      handleScanError(err, setError);
    } finally {
      setLoading(false);
    }
  };

  // ============================================================================
  // MANUAL SELECTION MODE HANDLERS
  // ============================================================================

  const handleManualScan = async (strategy, customParams = null) => {
    // Check if market is open
    const marketStatus = isMarketOpen();
    if (!marketStatus.isOpen) {
      console.log('⚠️ Market is closed:', marketStatus.reason);
      setTradingHoursDayType(marketStatus.reason);
      setIsTradingHoursModalOpen(true);
      return;
    }
    
    const isBreakout = strategy === 'breakout';
    const setLoading = isBreakout ? setIsBreakoutLoading : setIsBreakdownLoading;
    const setError = isBreakout ? setBreakoutError : setBreakdownError;
    const setResult = isBreakout ? setBreakoutResult : setBreakdownResult;
    const setCandidates = isBreakout ? setBreakoutCandidates : setBreakdownCandidates;
    
    setLoading(true);
    setError(null);
    setResult(null); // Clear automatic results
    setCandidates([]);
    
    try {
      console.log(`🔍 Starting MANUAL ${strategy.toUpperCase()} Scan...`);
      
      const requestBody = {
        strategy_type: strategy,
        ...(customParams || {}),
        top_n: 3
      };
      
      const response = await api.post('/api/v1/fno/scan-for-manual-selection', requestBody, {
        timeout: 120000 // 2 minutes timeout
      });
      
      console.log(`✅ Manual ${strategy} scan completed:`, response.data);
      
      if (response.data.success && response.data.top_candidates && response.data.top_candidates.length > 0) {
        setCandidates(response.data.top_candidates);
      } else {
        setError(response.data.error || 'No candidates found. Try adjusting your parameters.');
      }
      
    } catch (err) {
      console.error(`❌ Manual ${strategy} scan failed:`, err);
      handleScanError(err, setError);
    } finally {
      setLoading(false);
    }
  };

  const handlePlaceManualOrder = async (candidate, strategy) => {
    const isBreakout = strategy === 'breakout';
    const setError = isBreakout ? setBreakoutError : setBreakdownError;
    const setResult = isBreakout ? setBreakoutResult : setBreakdownResult;
    const setCandidates = isBreakout ? setBreakoutCandidates : setBreakdownCandidates;
    
    setPlacingOrderFor(candidate.symbol);
    setError(null);
    
    try {
      console.log(`📝 Placing manual order for ${candidate.symbol}...`);
      
      const response = await api.post('/api/v1/fno/place-manual-order', {
        strategy_type: strategy,
        candidate: candidate
      });
      
      console.log(`✅ Manual order placed:`, response.data);
      
      if (response.data.success) {
        // Clear candidates and show result
        setCandidates([]);
        // Convert manual order result to automatic result format for display
        setResult({
          success: true,
          timestamp: response.data.timestamp,
          scan_summary: {
            total_stocks_scanned: 155, // Default value
            gainers_found: 1,
            losers_found: 1,
            valid_candidates: 1,
            best_candidate: {
              symbol: candidate.symbol,
              pct_change: candidate.pct_change,
              volume: candidate.volume,
              option_symbol: candidate.option_symbol,
              option_premium: candidate.option_ltp
            }
          },
          order_result: response.data.order_result,
          funds_check: response.data.funds_check
        });
      } else {
        setError(response.data.order_result?.message || 'Failed to place order');
      }
      
    } catch (err) {
      console.error(`❌ Failed to place manual order:`, err);
      handleScanError(err, setError);
    } finally {
      setPlacingOrderFor(null);
    }
  };

  // ============================================================================
  // ORDER STATUS SYNC
  // ============================================================================

  const syncOrderStatus = useCallback(async (isAutomatic = false) => {
    if (isSyncingStatus) return; // Prevent multiple simultaneous syncs
    
    setIsSyncingStatus(true);
    
    try {
      const response = await api.post('/api/v1/trading/sync-gtt-orders', {}, {
        params: { broker: 'ZERODHA' }
      });
      
      console.log('✅ Order status synced:', response.data);
      setLastSyncTime(new Date());
      
      // If any orders were cancelled, update the UI
      if (response.data.cancelled_orders_count > 0 && !isAutomatic) {
        // Update result status if currently showing buy_gtt_placed
        if (breakoutResult?.order_result?.status === 'buy_gtt_placed') {
          setBreakoutResult(prev => ({
            ...prev,
            order_result: {
              ...prev.order_result,
              status: 'buy_gtt_cancelled',
              message: 'BUY GTT order was cancelled on Zerodha. The exit GTT will not be placed.'
            }
          }));
        }
        
        if (breakdownResult?.order_result?.status === 'buy_gtt_placed') {
          setBreakdownResult(prev => ({
            ...prev,
            order_result: {
              ...prev.order_result,
              status: 'buy_gtt_cancelled',
              message: 'BUY GTT order was cancelled on Zerodha. The exit GTT will not be placed.'
            }
          }));
        }
      }
      
      return response.data;
    } catch (err) {
      console.error('❌ Failed to sync order status:', err);
      if (!isAutomatic) {
        // Only show error if manually triggered
        const errorMessage = err.response?.data?.detail || 'Failed to sync order status';
        if (breakoutResult) setBreakoutError(errorMessage);
        if (breakdownResult) setBreakdownError(errorMessage);
      }
    } finally {
      setIsSyncingStatus(false);
    }
  }, [isSyncingStatus, breakoutResult, breakdownResult]);

  // Auto-sync every 30 seconds if there's an active order
  useEffect(() => {
    const hasActiveOrder = 
      (breakoutResult?.order_result?.status === 'buy_gtt_placed') ||
      (breakdownResult?.order_result?.status === 'buy_gtt_placed');
    
    if (!hasActiveOrder) return;
    
    // Initial sync after 5 seconds
    const initialTimeout = setTimeout(() => {
      syncOrderStatus(true);
    }, 5000);
    
    // Then sync every 30 seconds
    const interval = setInterval(() => {
      syncOrderStatus(true);
    }, 30000);
    
    return () => {
      clearTimeout(initialTimeout);
      clearInterval(interval);
    };
  }, [breakoutResult, breakdownResult, syncOrderStatus]);

  // ============================================================================
  // HELPER FUNCTIONS
  // ============================================================================

  const handleScanError = (err, setError) => {
    if (err.response?.status === 404) {
      setError('Broker not connected. Please connect your Zerodha account first.');
    } else if (err.response?.status === 401) {
      setError('Session expired. Please login again.');
    } else if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
      setError('Scan timed out. The scan takes longer during market hours due to thorough analysis. Please try again.');
    } else if (err.response?.data?.detail) {
      setError(err.response.data.detail);
    } else {
      setError(`Scan encountered an error: ${err.message || 'Please try again.'}`);
    }
  };

  const handleOpenModifyModal = (strategy, mode = 'automatic') => {
    setCurrentStrategy(strategy);
    setCurrentMode(mode);
    setIsModifyModalOpen(true);
  };

  const handleCustomScan = (params, mode) => {
    if (mode === 'automatic') {
      handleAutoScan(currentStrategy, params);
    } else {
      handleManualScan(currentStrategy, params);
    }
  };

  const handleScheduleCreated = () => {
    setRefreshSchedules(prev => prev + 1);
  };

  const renderStatusBadge = (status) => {
    const statusConfig = {
      order_placed: {
        color: 'green',
        icon: <FaCheckCircle />,
        label: 'Order Placed'
      },
      buy_gtt_placed: {
        color: 'blue',
        icon: <FaSpinner />,
        label: 'BUY GTT Active'
      },
      buy_gtt_cancelled: {
        color: 'red',
        icon: <FaExclamationTriangle />,
        label: 'BUY GTT Cancelled'
      },
      insufficient_funds: {
        color: 'yellow',
        icon: <FaWallet />,
        label: 'Insufficient Funds'
      },
      no_candidates: {
        color: 'gray',
        icon: <FaList />,
        label: 'No Movers Found'
      },
      no_valid_setups: {
        color: 'gray',
        icon: <FaList />,
        label: 'No Valid Setups'
      },
      order_failed: {
        color: 'red',
        icon: <FaExclamationTriangle />,
        label: 'Order Failed'
      },
      error: {
        color: 'red',
        icon: <FaExclamationTriangle />,
        label: 'Error'
      }
    };

    const config = statusConfig[status] || statusConfig.error;

    return (
      <span className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium bg-${config.color}-100 text-${config.color}-800`}>
        {config.icon}
        {config.label}
      </span>
    );
  };

  // ============================================================================
  // RENDER FUNCTIONS
  // ============================================================================

  const renderCandidateCard = (candidate, strategy) => {
    const isBreakout = strategy === 'breakout';
    const isPlacingOrder = placingOrderFor === candidate.symbol;
    
    return (
      <motion.div
        key={candidate.symbol}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className={`bg-white rounded-lg shadow-lg p-6 border-2 ${
          candidate.rank === 1 
            ? (isBreakout ? 'border-green-400' : 'border-red-400')
            : 'border-gray-200'
        }`}
      >
        {/* Rank Badge */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-lg ${
              candidate.rank === 1 
                ? (isBreakout ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700')
                : 'bg-gray-100 text-gray-700'
            }`}>
              #{candidate.rank}
            </div>
            <div>
              <h3 className="text-2xl font-bold text-gray-900">{candidate.symbol}</h3>
              <p className="text-sm text-gray-600">{candidate.option_type === 'CE' ? 'Call Option' : 'Put Option'}</p>
            </div>
          </div>
          {candidate.rank === 1 && (
            <FaTrophy className={`text-3xl ${isBreakout ? 'text-green-500' : 'text-red-500'}`} />
          )}
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div className="bg-gray-50 rounded-lg p-3">
            <p className="text-xs text-gray-600 mb-1">Price Change</p>
            <p className={`text-lg font-bold ${isBreakout ? 'text-green-600' : 'text-red-600'}`}>
              {candidate.pct_change > 0 ? '+' : ''}{candidate.pct_change}%
            </p>
          </div>
          <div className="bg-gray-50 rounded-lg p-3">
            <p className="text-xs text-gray-600 mb-1">Volume</p>
            <p className="text-lg font-bold text-gray-900">
              {candidate.volume.toLocaleString()}
            </p>
          </div>
          <div className="bg-gray-50 rounded-lg p-3">
            <p className="text-xs text-gray-600 mb-1">15-min RSI</p>
            <p className="text-lg font-bold text-blue-600">
              {candidate.rsi_15min?.toFixed(1) || 'N/A'}
            </p>
          </div>
          <div className="bg-gray-50 rounded-lg p-3">
            <p className="text-xs text-gray-600 mb-1">Daily RSI</p>
            <p className="text-lg font-bold text-purple-600">
              {candidate.rsi_daily?.toFixed(1) || 'N/A'}
            </p>
          </div>
        </div>

        {/* Option Details */}
        <div className="bg-blue-50 rounded-lg p-4 mb-4">
          <h4 className="font-semibold text-blue-900 mb-2 text-sm">Option Details</h4>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">Symbol:</span>
              <span className="font-mono text-xs text-gray-900">{candidate.option_symbol}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Strike:</span>
              <span className="font-semibold text-gray-900">₹{candidate.option_strike}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Premium (LTP):</span>
              <span className="font-semibold text-gray-900">₹{candidate.option_ltp}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Lot Size:</span>
              <span className="font-semibold text-gray-900">{candidate.lot_size}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Total Quantity:</span>
              <span className="font-semibold text-gray-900">{candidate.quantity}</span>
            </div>
          </div>
        </div>

        {/* Order Parameters */}
        <div className={`${isBreakout ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'} border rounded-lg p-4 mb-4`}>
          <h4 className={`font-semibold ${isBreakout ? 'text-green-900' : 'text-red-900'} mb-2 text-sm`}>
            Order Parameters
          </h4>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <p className="text-gray-600 text-xs">Entry Price</p>
              <p className="font-semibold text-gray-900">₹{candidate.buy_price}</p>
            </div>
            <div>
              <p className="text-gray-600 text-xs">Required Margin</p>
              <p className="font-semibold text-gray-900">₹{candidate.required_margin.toLocaleString()}</p>
            </div>
            <div>
              <p className="text-gray-600 text-xs">Target Price</p>
              <p className="font-semibold text-green-600">
                ₹{candidate.target_price} 
                <span className="text-xs ml-1">
                  (+{((candidate.target_price / candidate.buy_price - 1) * 100).toFixed(1)}%)
                </span>
              </p>
            </div>
            <div>
              <p className="text-gray-600 text-xs">Stop Loss</p>
              <p className="font-semibold text-red-600">
                ₹{candidate.stop_loss_price}
                <span className="text-xs ml-1">
                  (-{((1 - candidate.stop_loss_price / candidate.buy_price) * 100).toFixed(1)}%)
                </span>
              </p>
            </div>
          </div>
        </div>

        {/* Potential P&L */}
        <div className="bg-gray-50 rounded-lg p-4 mb-4">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-gray-600 text-xs mb-1">Potential Profit</p>
              <p className="font-bold text-green-600 text-lg">
                +₹{((candidate.target_price - candidate.buy_price) * candidate.quantity).toLocaleString()}
              </p>
            </div>
            <div>
              <p className="text-gray-600 text-xs mb-1">Potential Loss</p>
              <p className="font-bold text-red-600 text-lg">
                -₹{((candidate.buy_price - candidate.stop_loss_price) * candidate.quantity).toLocaleString()}
              </p>
            </div>
          </div>
        </div>

        {/* Place Order Button */}
        <button
          onClick={() => handlePlaceManualOrder(candidate, strategy)}
          disabled={isPlacingOrder}
          className={`w-full flex items-center justify-center gap-3 px-6 py-4 rounded-lg text-base font-semibold
            transition-all duration-300 shadow-lg ${
              isPlacingOrder
                ? 'bg-gray-400 cursor-not-allowed'
                : isBreakout
                ? 'bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white hover:shadow-xl'
                : 'bg-gradient-to-r from-red-600 to-rose-600 hover:from-red-700 hover:to-rose-700 text-white hover:shadow-xl'
            }`}
        >
          {isPlacingOrder ? (
            <>
              <FaSpinner className="animate-spin text-xl" />
              Placing Order...
            </>
          ) : (
            <>
              <FaRocket className="text-xl" />
              Place This Order
            </>
          )}
        </button>
      </motion.div>
    );
  };

  const renderResultCard = (result, type) => {
    const isBreakout = type === 'breakout';
    
    // Handle both automatic mode and manual mode results
    const scanSummary = result.scan_result?.scan_summary || result.scan_summary;
    const orderResult = result.order_result;
    const fundsCheck = result.funds_check;
    
    // Check if this is a "no stocks found" scenario (not an error, just no opportunities)
    const isNoStocksFound = !result.success && result.error && (
      result.error.includes('No stocks found with required momentum') ||
      (result.error.includes('No valid') && result.error.includes('setups found'))
    );
    
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="space-y-6 mt-6"
      >
        {/* Summary Cards */}
        {scanSummary && (
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-white rounded-lg shadow p-4">
              <h3 className="text-xs font-medium text-gray-500 mb-2">Stocks Scanned</h3>
              <p className="text-2xl font-bold text-gray-900">
                {scanSummary.total_stocks_scanned || 155}
              </p>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <h3 className="text-xs font-medium text-gray-500 mb-2">{isBreakout ? 'Gainers' : 'Losers'} Found</h3>
              <p className={`text-2xl font-bold ${isBreakout ? 'text-green-600' : 'text-red-600'}`}>
                {scanSummary.gainers_found || scanSummary.losers_found || scanSummary.movers_found || 0}
              </p>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <h3 className="text-xs font-medium text-gray-500 mb-2">Valid Candidates</h3>
              <p className="text-2xl font-bold text-blue-600">
                {scanSummary.valid_candidates || 0}
              </p>
            </div>
          </div>
        )}

        {/* Best Candidate Card */}
        {scanSummary?.best_candidate && (
          <div className={`bg-gradient-to-r ${isBreakout ? 'from-green-50 to-emerald-50 border-green-300' : 'from-red-50 to-rose-50 border-red-300'} border-2 rounded-lg shadow-lg p-4`}>
            <div className="flex items-center gap-3 mb-3">
              <FaTrophy className={`text-2xl ${isBreakout ? 'text-green-600' : 'text-red-600'}`} />
              <h3 className="text-lg font-bold text-gray-900">Best Candidate Selected</h3>
            </div>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <p className="text-xs text-gray-600 mb-1">Stock</p>
                <p className="text-xl font-bold text-gray-900">
                  {scanSummary.best_candidate.symbol}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-600 mb-1">Change</p>
                <p className={`text-xl font-bold ${isBreakout ? 'text-green-600' : 'text-red-600'}`}>
                  {scanSummary.best_candidate.pct_change}%
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-600 mb-1">Volume</p>
                <p className="text-sm font-semibold text-gray-900">
                  {scanSummary.best_candidate.volume?.toLocaleString()}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-600 mb-1">Option</p>
                <p className="text-xs font-medium text-gray-900">
                  {scanSummary.best_candidate.option_symbol}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Order Result Card */}
        <div className="bg-white rounded-lg shadow-lg p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-gray-900">
              {isNoStocksFound ? 'Scan Result' : 'Order Result'}
            </h3>
            <div className="flex items-center gap-3">
              {!isNoStocksFound && orderResult?.status === 'buy_gtt_placed' && (
                <button
                  onClick={() => syncOrderStatus(false)}
                  disabled={isSyncingStatus}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                    isSyncingStatus
                      ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                      : 'bg-blue-50 text-blue-700 hover:bg-blue-100'
                  }`}
                  title="Check if order status has changed on Zerodha"
                >
                  <FaSpinner className={isSyncingStatus ? 'animate-spin' : ''} />
                  {isSyncingStatus ? 'Checking...' : 'Check Status'}
                </button>
              )}
              {isNoStocksFound 
                ? renderStatusBadge(scanSummary?.movers_found === 0 ? 'no_candidates' : 'no_valid_setups')
                : renderStatusBadge(orderResult?.status || 'pending')
              }
            </div>
          </div>

          <p className="text-sm text-gray-700 mb-4">
            {isNoStocksFound 
              ? (scanSummary?.movers_found === 0 
                  ? `No stocks found with ${isBreakout ? 'bullish' : 'bearish'} momentum matching the criteria. The market conditions may not be favorable for ${isBreakout ? 'breakout' : 'breakdown'} opportunities at this time.`
                  : `Found ${scanSummary?.movers_found || 0} ${isBreakout ? 'gainers' : 'losers'}, but none had valid option setups near their day high. Try adjusting your parameters or wait for better market conditions.`
                )
              : (orderResult?.message || 'Processing...')
            }
          </p>

          {/* Auto-sync notification */}
          {!isNoStocksFound && orderResult?.status === 'buy_gtt_placed' && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4 flex items-center gap-2">
              <FaSpinner className="text-blue-600 text-sm animate-spin" />
              <p className="text-xs text-blue-800">
                <strong>Auto-checking:</strong> Order status is being verified every 30 seconds. 
                {lastSyncTime && (
                  <span className="ml-1">Last checked: {lastSyncTime.toLocaleTimeString()}</span>
                )}
              </p>
            </div>
          )}

          {/* Cancellation notice */}
          {!isNoStocksFound && orderResult?.status === 'buy_gtt_cancelled' && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4 flex items-start gap-2">
              <FaExclamationTriangle className="text-red-600 text-sm mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-xs text-red-800">
                  <strong>Order Cancelled:</strong> This GTT order was cancelled on Zerodha. 
                  No exit GTT will be placed. If you want to place a new order, please run the scan again.
                </p>
              </div>
            </div>
          )}

          {/* Funds Check */}
          {fundsCheck && (
            <div className="bg-gray-50 rounded-lg p-3 mb-4">
              <h4 className="font-semibold text-gray-900 mb-2 text-sm">Funds Check</h4>
              <div className="grid grid-cols-3 gap-3 text-xs">
                <div>
                  <p className="text-gray-600">Available</p>
                  <p className="text-sm font-semibold text-gray-900">
                    ₹{fundsCheck.available_cash?.toLocaleString()}
                  </p>
                </div>
                <div>
                  <p className="text-gray-600">Required</p>
                  <p className="text-sm font-semibold text-gray-900">
                    ₹{fundsCheck.required_margin?.toLocaleString()}
                  </p>
                </div>
                <div>
                  <p className="text-gray-600">Status</p>
                  <p className={`text-sm font-semibold ${fundsCheck.sufficient ? 'text-green-600' : 'text-red-600'}`}>
                    {fundsCheck.sufficient ? 'OK ✓' : 'Low ✗'}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Order Details */}
          {orderResult?.order_details && (
            <div className={`${isBreakout ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'} border rounded-lg p-3`}>
              <h4 className={`font-semibold ${isBreakout ? 'text-green-900' : 'text-red-900'} mb-2 flex items-center gap-2 text-sm`}>
                <FaCheckCircle />
                Order Details
              </h4>
              <div className="grid grid-cols-2 gap-3 text-xs">
                <div>
                  <p className="text-gray-600">BUY GTT ID</p>
                  <p className="font-mono font-semibold text-gray-900 text-xs">
                    {orderResult.buy_gtt_id || orderResult.gtt_id}
                  </p>
                </div>
                <div>
                  <p className="text-gray-600">Stock</p>
                  <p className="font-semibold text-gray-900">
                    {orderResult.order_details.symbol}
                  </p>
                </div>
                <div className="col-span-2">
                  <p className="text-gray-600">Option</p>
                  <p className="font-mono text-xs text-gray-900">
                    {orderResult.order_details.option_symbol}
                  </p>
                </div>
                <div>
                  <p className="text-gray-600">Lots</p>
                  <p className="font-semibold text-gray-900">
                    {orderResult.order_details.lots || 1} Lot(s)
                  </p>
                </div>
                <div>
                  <p className="text-gray-600">Total Quantity</p>
                  <p className="font-semibold text-gray-900">
                    {orderResult.order_details.quantity} <span className="text-xs text-gray-500">({orderResult.order_details.lot_size}/lot)</span>
                  </p>
                </div>
                <div>
                  <p className="text-gray-600">Target</p>
                  <p className="font-semibold text-green-600">
                    ₹{orderResult.order_details.target_price}
                  </p>
                </div>
                <div>
                  <p className="text-gray-600">Stop-Loss</p>
                  <p className="font-semibold text-red-600">
                    ₹{orderResult.order_details.stop_loss_price}
                  </p>
                </div>
                <div>
                  <p className="text-gray-600">Potential Profit</p>
                  <p className="font-semibold text-green-600 text-xs">
                    +₹{orderResult.order_details.potential_profit?.toLocaleString()}
                  </p>
                </div>
                <div>
                  <p className="text-gray-600">Potential Loss</p>
                  <p className="font-semibold text-red-600 text-xs">
                    -₹{orderResult.order_details.potential_loss?.toLocaleString()}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Timestamp */}
        <p className="text-center text-xs text-gray-500 mt-4">
          Scan completed at: {new Date(result.timestamp).toLocaleString()}
        </p>
      </motion.div>
    );
  };

  const renderTraderCard = (strategy) => {
    const isBreakout = strategy === 'breakout';
    const loading = isBreakout ? isBreakoutLoading : isBreakdownLoading;
    const error = isBreakout ? breakoutError : breakdownError;
    const result = isBreakout ? breakoutResult : breakdownResult;
    const candidates = isBreakout ? breakoutCandidates : breakdownCandidates;

    return (
      <motion.div
        initial={{ opacity: 0, x: isBreakout ? -20 : 20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: isBreakout ? 0.1 : 0.2 }}
        className={`bg-white rounded-lg shadow-xl p-6 border-t-4 ${isBreakout ? 'border-green-500' : 'border-red-500'}`}
      >
        {/* Header */}
        <div className="flex items-center gap-3 mb-4">
          <div className={`w-12 h-12 ${isBreakout ? 'bg-green-100' : 'bg-red-100'} rounded-full flex items-center justify-center`}>
            {isBreakout ? (
              <HiOutlineTrendingUp className="text-2xl text-green-600" />
            ) : (
              <HiOutlineTrendingDown className="text-2xl text-red-600" />
            )}
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-900">
              {isBreakout ? 'Breakout' : 'Breakdown'} Trader
            </h2>
            <p className="text-sm text-gray-600">
              {isBreakout ? 'Bullish' : 'Bearish'} Momentum Strategy
            </p>
          </div>
        </div>

        <p className="text-sm text-gray-700 mb-6">
          {isBreakout 
            ? 'Scans for stocks with 3% gain, finds ATM Call options near their day high, and places automated GTT orders.'
            : 'Scans for stocks with 3% loss and finds ATM Put options near their day high, and places automated GTT orders.'
          }
        </p>

        {/* Strategy Steps */}
        <div className="space-y-3 mb-6">
          <div className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg">
            <span className="flex-shrink-0 w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center text-xs font-bold">1</span>
            <div>
              <p className="font-semibold text-blue-900 text-sm">Scan</p>
              <p className="text-xs text-blue-800">
                Find F&O stocks with 3% {isBreakout ? 'gain' : 'loss'}
              </p>
            </div>
          </div>
          <div className={`flex items-start gap-3 p-3 ${isBreakout ? 'bg-green-50' : 'bg-orange-50'} rounded-lg`}>
            <span className={`flex-shrink-0 w-6 h-6 ${isBreakout ? 'bg-green-500' : 'bg-orange-500'} text-white rounded-full flex items-center justify-center text-xs font-bold`}>2</span>
            <div>
              <p className={`font-semibold ${isBreakout ? 'text-green-900' : 'text-orange-900'} text-sm`}>Analyze</p>
              <p className={`text-xs ${isBreakout ? 'text-green-800' : 'text-orange-800'}`}>
                Find ATM {isBreakout ? 'Call (CE)' : 'Put (PE)'} options near day high
              </p>
            </div>
          </div>
          <div className={`flex items-start gap-3 p-3 ${isBreakout ? 'bg-purple-50' : 'bg-red-50'} rounded-lg`}>
            <span className={`flex-shrink-0 w-6 h-6 ${isBreakout ? 'bg-purple-500' : 'bg-red-500'} text-white rounded-full flex items-center justify-center text-xs font-bold`}>3</span>
            <div>
              <p className={`font-semibold ${isBreakout ? 'text-purple-900' : 'text-red-900'} text-sm`}>Trade</p>
              <p className={`text-xs ${isBreakout ? 'text-purple-800' : 'text-red-800'}`}>
                Place GTT for 1 lot (10% target, 2% SL)
              </p>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="space-y-3 mb-4">
          {/* Automatic Mode Button */}
          <button
            onClick={() => handleAutoScan(strategy)}
            disabled={loading}
            className={`
              w-full flex items-center justify-center gap-3 px-6 py-4 rounded-lg text-base font-semibold
              transition-all duration-300 shadow-lg
              ${loading
                ? 'bg-gray-400 cursor-not-allowed'
                : isBreakout
                ? 'bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white hover:shadow-xl'
                : 'bg-gradient-to-r from-red-600 to-rose-600 hover:from-red-700 hover:to-rose-700 text-white hover:shadow-xl'
              }
            `}
          >
            {loading ? (
              <>
                <FaSpinner className="animate-spin text-xl" />
                Scanning...
              </>
            ) : (
              <>
                <FaRocket className="text-xl" />
                Run Auto Scan & Trade
              </>
            )}
          </button>

          {/* Manual Mode Button */}
          <button
            onClick={() => handleManualScan(strategy)}
            disabled={loading}
            className={`
              w-full flex items-center justify-center gap-3 px-6 py-3 rounded-lg text-base font-semibold
              transition-all duration-300 border-2
              ${loading
                ? 'border-gray-300 text-gray-400 cursor-not-allowed'
                : isBreakout
                ? 'border-green-600 text-green-700 hover:bg-green-50'
                : 'border-red-600 text-red-700 hover:bg-red-50'
              }
            `}
          >
            <FaHandPointer className="text-lg" />
            Scan for Manual Selection
          </button>
        </div>

        {/* Modify Scan Button */}
        <button
          onClick={() => handleOpenModifyModal(strategy)}
          disabled={loading}
          className={`w-full ${isBreakout ? 'text-green-700 hover:text-green-800' : 'text-red-700 hover:text-red-800'} font-medium text-sm py-2 transition-colors disabled:text-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2`}
        >
          <FaCog />
          Modify your {isBreakout ? 'Breakout' : 'Breakdown'} Scan
        </button>

        {/* Error Display */}
        {error && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className={`${
              (error.includes('No stocks found') || (error.includes('No') && error.includes('found')))
                ? 'bg-blue-50 border-l-4 border-blue-500'
                : 'bg-red-50 border-l-4 border-red-500'
            } p-4 rounded mt-4`}
          >
            <div className="flex items-start gap-2">
              {(error.includes('No stocks found') || (error.includes('No') && error.includes('found'))) ? (
                <FaList className="text-blue-500 text-lg flex-shrink-0 mt-1" />
              ) : (
                <FaExclamationTriangle className="text-red-500 text-lg flex-shrink-0 mt-1" />
              )}
              <div>
                <h3 className={`text-sm font-semibold mb-1 ${
                  (error.includes('No stocks found') || (error.includes('No') && error.includes('found')))
                    ? 'text-blue-900'
                    : 'text-red-900'
                }`}>
                  {(error.includes('No stocks found') || (error.includes('No') && error.includes('found')))
                    ? 'No Opportunities Found'
                    : 'Error'
                  }
                </h3>
                <p className={`text-xs ${
                  (error.includes('No stocks found') || (error.includes('No') && error.includes('found')))
                    ? 'text-blue-700'
                    : 'text-red-700'
                }`}>
                  {(error.includes('No stocks found') || (error.includes('No') && error.includes('found')))
                    ? `${error} This is normal market behavior. Try again later or adjust your scan parameters.`
                    : error
                  }
                </p>
              </div>
            </div>
          </motion.div>
        )}

        {/* Manual Selection UI - Candidates */}
        {candidates.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-6"
          >
            <div className={`${isBreakout ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'} border-2 rounded-lg p-4 mb-4`}>
              <h3 className={`text-lg font-bold ${isBreakout ? 'text-green-900' : 'text-red-900'} mb-2 flex items-center gap-2`}>
                <FaTrophy className="text-xl" />
                Top {candidates.length} Candidates Found
              </h3>
              <p className="text-sm text-gray-700">
                Review the candidates below and select one to place an order
              </p>
            </div>

            <div className="space-y-4">
              {candidates.map((candidate) => renderCandidateCard(candidate, strategy))}
            </div>
          </motion.div>
        )}

        {/* Results (Automatic Mode) */}
        {result && renderResultCard(result, strategy)}
      </motion.div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      <Navbar />
      
      <div className="container mx-auto px-4 py-8 max-w-7xl">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <h1 className="text-4xl font-bold text-gray-900 mb-3">
            F&O Momentum Trader
          </h1>
          <p className="text-lg text-gray-600 max-w-4xl mx-auto">
            Choose your trading mode: <strong>Automatic</strong> for one-click trading or <strong>Manual Selection</strong> to review top 3 candidates.
          </p>
        </motion.div>

        {/* Two-Column Grid for Breakout and Breakdown */}
        <div className="grid lg:grid-cols-2 gap-8">
          {renderTraderCard('breakout')}
          {renderTraderCard('breakdown')}
        </div>

        {/* Instructions (shown when no results) */}
        {!breakoutResult && !breakdownResult && !isBreakoutLoading && !isBreakdownLoading && 
         breakoutCandidates.length === 0 && breakdownCandidates.length === 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white rounded-lg shadow-lg p-6 mt-8"
          >
            <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <FaChartLine className="text-blue-600" />
              How It Works
            </h3>
            
            {/* Mode Comparison */}
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div className="border-2 border-green-500 rounded-lg p-4">
                <h4 className="font-semibold text-green-900 mb-2 flex items-center gap-2">
                  <FaRocket />
                  Automatic Mode
                </h4>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li>✅ One-click trading</li>
                  <li>✅ System selects best candidate</li>
                  <li>✅ Order placed automatically</li>
                  <li>✅ Fast execution</li>
                </ul>
              </div>
              <div className="border-2 border-blue-500 rounded-lg p-4">
                <h4 className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
                  <FaHandPointer />
                  Manual Selection Mode
                </h4>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li>✅ Review top 3 candidates</li>
                  <li>✅ Compare all options</li>
                  <li>✅ Choose which to trade</li>
                  <li>✅ Informed decisions</li>
                </ul>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="border-l-4 border-green-500 pl-4">
                <h4 className="font-semibold text-green-900 mb-2">Breakout Strategy (Bullish)</h4>
                <ol className="space-y-2 text-sm text-gray-700">
                  <li>✅ Scans for stocks with 3% gain</li>
                  <li>✅ Filters by RSI indicators</li>
                  <li>✅ Finds ATM Call options near their high</li>
                  <li>✅ Ranks by volume and momentum</li>
                  <li>✅ Places GTT order with 10% target & 2% SL</li>
                </ol>
              </div>
              <div className="border-l-4 border-red-500 pl-4">
                <h4 className="font-semibold text-red-900 mb-2">Breakdown Strategy (Bearish)</h4>
                <ol className="space-y-2 text-sm text-gray-700">
                  <li>✅ Scans for stocks with 3% loss</li>
                  <li>✅ Filters by RSI indicators</li>
                  <li>✅ Finds ATM Put options near their high</li>
                  <li>✅ Ranks by volume and momentum</li>
                  <li>✅ Places GTT order with 10% target & 2% SL</li>
                </ol>
              </div>
            </div>

            <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-900">
                <strong>💡 Pro Tip:</strong> Use <strong>Manual Selection</strong> when you want to analyze multiple options, 
                or <strong>Automatic Mode</strong> for fast execution when you trust the system's ranking.
              </p>
            </div>
          </motion.div>
        )}

        {/* Scheduled Scans Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="mt-8"
        >
          <ScheduledScansSection refreshTrigger={refreshSchedules} />
        </motion.div>
      </div>

      <Footer />
      
      {/* Modify Scan Modal */}
      <ModifyScanModal
        isOpen={isModifyModalOpen}
        onClose={() => setIsModifyModalOpen(false)}
        strategyType={currentStrategy}
        executionMode={currentMode}
        onStartScan={handleCustomScan}
        onScheduleCreated={handleScheduleCreated}
        hideSessionExpiredError={showPageLevelBrokerModal}
      />
      
      {/* Trading Hours Modal */}
      <TradingHoursModal
        isOpen={isTradingHoursModalOpen}
        onClose={() => setIsTradingHoursModalOpen(false)}
        dayType={tradingHoursDayType}
      />
      
      {/* Broker Session Expired Modal (Page Level) */}
      {showPageLevelBrokerModal && <ReconnectBrokerModal />}
    </div>
  );
};

export default FnoTraderPage;
